import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { motion, AnimatePresence } from 'motion/react';
import { auth } from '../lib/firebase';
import { ArrowRight, Lock, Mail, ShieldCheck, CheckCircle2, User as UserIcon } from 'lucide-react';

import { dbService } from '../services/dbService';

export default function AuthPage() {
  const [isSignIn, setIsSignIn] = useState(true);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !user.emailVerified) {
        setNeedsVerification(true);
        setEmail(user.email || '');
      } else if (user && user.emailVerified) {
        await dbService.ensureUserProfile(user.uid, user.email, user.displayName);
      }
    });
    return unsubscribe;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignIn) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        if (!user.emailVerified) {
          setNeedsVerification(true);
          setLoading(false);
          return;
        }
        await dbService.ensureUserProfile(user.uid, user.email, user.displayName);
        navigate('/dashboard');
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        // Update profile with name if provided
        if (fullName) {
          await updateProfile(user, { displayName: fullName });
        }
        
        await dbService.ensureUserProfile(user.uid, user.email, fullName);
        await sendEmailVerification(user);
        setNeedsVerification(true);
      }
    } catch (err: any) {
      console.error(err);
      if (isSignIn) {
        setError('Email or password is incorrect');
      } else {
        if (err.code === 'auth/email-already-in-use') {
          setError('User already exists. Please sign in');
        } else {
          setError(err.message || 'An error occurred during sign up');
        }
      }
    } finally {
      if (!isSignIn || error || (isSignIn && needsVerification)) {
        setLoading(false);
      }
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Google accounts are usually verified, but check for consistency
      if (!user.emailVerified) {
        setNeedsVerification(true);
        setLoading(false);
        return;
      }
      await dbService.ensureUserProfile(user.uid, user.email, user.displayName);
      navigate('/dashboard');
    } catch (err: any) {
      console.error(err);
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message || 'An error occurred during Google sign in');
      }
    } finally {
      setLoading(false);
    }
  };

  if (needsVerification) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center bg-theme-bg px-6">
        <div className="max-w-md w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-black/20 p-8 md:p-12 border border-gold/30 backdrop-blur-sm shadow-2xl relative"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gold"></div>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 flex items-center justify-center border border-gold/40 bg-gold/5 mb-6">
                <CheckCircle2 className="text-gold h-8 w-8" />
              </div>
              <h1 className="text-2xl font-serif italic text-theme-text uppercase tracking-widest mb-6">
                Verification Required
              </h1>
              <p className="text-taupe text-sm leading-relaxed mb-8">
                We have sent you a verification email to <span className="text-gold font-medium">{email}</span>. Please verify it and log in.
              </p>
              <button
                onClick={async () => {
                  setNeedsVerification(false);
                  setIsSignIn(true);
                  setError('');
                  setEmail('');
                  setPassword('');
                  await signOut(auth);
                }}
                className="btn-gold w-full flex justify-center items-center space-x-3 group"
              >
                <span>Login</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 flex items-center justify-center bg-theme-bg px-6">
      <div className="max-w-md w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-black/20 p-8 md:p-12 border border-gold/30 backdrop-blur-sm shadow-2xl relative"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gold"></div>
          
          <div className="flex flex-col items-center mb-10">
            <div className="w-16 h-16 flex items-center justify-center border border-gold/40 bg-gold/5 mb-6">
              <Lock className="text-gold h-6 w-6" />
            </div>
            <h1 className="text-2xl font-serif italic text-theme-text uppercase tracking-widest text-center">
              {isSignIn ? 'Member Access' : 'Secure Registration'}
            </h1>
            <p className="text-taupe text-[10px] uppercase tracking-widest mt-2">
              Relational Infrastructure Portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isSignIn && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-2"
              >
                <label className="text-[10px] uppercase tracking-[0.2em] text-taupe font-semibold ml-1">Legal Full Name</label>
                <div className="relative group">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-taupe transition-colors group-focus-within:text-gold" />
                  <input
                    type="text"
                    required={!isSignIn}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-gold transition-all placeholder:text-taupe/30"
                    placeholder="E.g. Alexandra Sterling"
                  />
                </div>
              </motion.div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-taupe font-semibold ml-1">Email Identifier</label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-taupe transition-colors group-focus-within:text-gold" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-gold transition-all placeholder:text-taupe/30"
                  placeholder="name@domain.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-taupe font-semibold ml-1">Secure Key</label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-taupe transition-colors group-focus-within:text-gold" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-gold transition-all placeholder:text-taupe/30"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-red-900/10 border border-red-900/30 p-4"
                >
                  <p className="text-red-400 text-xs tracking-wide italic text-center">
                    {error}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full flex justify-center items-center space-x-3 group"
            >
              <span>{loading ? 'Authenticating...' : (isSignIn ? 'Access Dashboard' : 'Register Securely')}</span>
              {!loading && <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
            </button>
          </form>

          <div className="mt-8 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-theme-border"></div>
            </div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-widest text-taupe">
              <span className="bg-[#1a1a1a] px-4 font-bold">Or Continue With</span>
            </div>
          </div>

          <div className="mt-8">
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center space-x-3 bg-white/5 border border-theme-border py-4 hover:bg-white/10 transition-all group"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
                <path fill="none" d="M0 0h24v24H0z" />
              </svg>
              <span className="text-xs uppercase tracking-widest font-bold">Google Identification</span>
            </button>
          </div>

          <div className="mt-10 pt-8 border-t border-theme-border text-center">
            <p className="text-taupe text-xs italic mb-4">
              {isSignIn ? "Don't have credentials?" : "Already registered?"}
            </p>
            <button
              onClick={() => {
                setIsSignIn(!isSignIn);
                setError('');
              }}
              className="text-gold text-[10px] uppercase tracking-widest font-bold hover:text-theme-text transition-colors pb-1 border-b border-gold/30 hover:border-theme-text"
            >
              {isSignIn ? 'Initiate Registration' : 'Return to Access Portal'}
            </button>
          </div>
        </motion.div>
        
        <div className="mt-12 flex items-center justify-center space-x-2 text-[10px] uppercase tracking-widest text-taupe/40 font-semibold">
          <ShieldCheck className="h-3 w-3" />
          <span>Encrypted Session // Level-3 Security Enabled</span>
        </div>
      </div>
    </div>
  );
}
